import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Cpu } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { id: "services", label: "Services" },
  { id: "industries", label: "Industries" },
  { id: "ai-agents", label: "AI Agents" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    // Close mobile menu first so its height doesn't offset the scroll target
    setOpen(false);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      // Account for fixed navbar height so section title isn't hidden under it
      const navH = window.innerWidth >= 1024 ? 80 : 64;
      const top = el.getBoundingClientRect().top + window.scrollY - navH + 1;
      window.scrollTo({ top, behavior: "smooth" });
    });
  };

  return (
    <motion.header
      data-testid={NAV.root}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <button
          data-testid={NAV.logo}
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-2.5 group"
        >
          <span className="relative w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 grid place-items-center shadow-lg shadow-cyan-500/20">
            <Cpu className="w-5 h-5 text-white" />
            <span className="absolute inset-0 rounded-lg ring-1 ring-white/20" />
          </span>
          <span className="font-heading font-bold text-lg sm:text-xl tracking-tight">
            Spoorti<span className="gradient-text"> Technologies</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={NAV.link(l.id)}
              onClick={() => scrollTo(l.id)}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-white/5 transition"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid={NAV.scheduleDemo}
            onClick={() => scrollTo("contact")}
            className="hidden sm:inline-flex btn-primary text-white px-5 py-2.5 rounded-full text-sm font-semibold"
          >
            Schedule a Demo
          </button>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 rounded-md hover:bg-white/5"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden glass border-t border-white/10 overflow-hidden"
          >
            <div className="px-4 py-3 flex flex-col">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="text-left px-3 py-2.5 text-slate-200 hover:text-white hover:bg-white/5 rounded-md"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("contact")}
                className="mt-2 btn-primary text-white px-5 py-2.5 rounded-full text-sm font-semibold"
              >
                Schedule a Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
